/**
 * V2 navigation menu — typed access to GET /v2/app/api/menu.
 *
 * The backend (V2Controller::_apiMenu -> Components_HeadMenu::getTree) returns
 * the permission-filtered HEAD_MENU tree in the session language: the SAME
 * source of truth as the legacy YUI menubar, so a user sees exactly the modules
 * and actions they have rights to.
 */
import { api } from "@/api/client";

/** A single actionable menu entry (module heading or one of its leaves). */
export interface MenuLeaf {
    /** HEAD_MENU id, e.g. "racuni_izdaniRacuni". Stable — safe as a key. */
    id: string;
    /** Display label in the session language, e.g. "Izdani računi". */
    label: string;
    /** Tab caption the legacy app used when opening the action (may be ""). */
    tabText: string;
    /** Legacy component name to open (falls back to `id` when unset). */
    action: string;
    /** True when the legacy action opened in a fresh window/tab. */
    newOpener: boolean;
}

/** A top-level module plus its flattened, ordered actionable leaves. */
export interface MenuModule extends MenuLeaf {
    items: MenuLeaf[];
}

export interface MenuResponse {
    status: true;
    /** Session language id, e.g. "SI". */
    language: string;
    /** The company (tenant, COMPANY_ID) the user is signed in to. */
    company?: { id: number; name: string };
    /** False when the user has a single company, so there is nothing to switch to. Missing = show it. */
    canSwitchCompany?: boolean;
    /**
     * The V2 screen rights the user holds ("v2_crm", "v2_adminPregledPf", ...) — HEAD_MENU
     * rows entered by hand and granted in PRAVICE_UPORABNIKOV, like every menu right.
     */
    rights: string[];
    viewAs: ViewAsState;
    /** True until a "Sam svoj računovodja" company has gone through the three first-time setup steps. */
    setupNeeded?: boolean;
    /** What the server saw when it kept the setup open (for the message at the last step). */
    setupDiag?: string;
    /** True when the company has a Stripe subscription ("Naročnina" in the sidebar). */
    hasSubscription?: boolean;
    /** True for a "Sam svoj računovodja" company (PAKET_ID = 1). */
    isSsr?: boolean;
    /** True when the user may switch between V1 and V2 ("Nazaj na V1" in the top bar). */
    canSwitchUi?: boolean;
    modules: MenuModule[];
}

/** Fetch the current user's permission-filtered navigation tree. */
export function fetchMenu(): Promise<MenuResponse> {
    return api.get<MenuResponse>("/menu");
}

/** The user's own choice between the classic (V1) and the new (V2) interface. */
export type UiVersion = "V1" | "V2";

/** Save the choice; `redirect` is where the chosen interface starts. */
export function saveUiVersion(version: UiVersion): Promise<{ status: true; version: UiVersion; redirect: string }> {
    return api.post("/uiVersion", { version });
}

/** "Poglej kot ..." — a superuser previewing the app as another user (read-only). */
export interface ViewAsState {
    /** True when the real login is a superuser, i.e. the switcher may be shown. */
    canSwitch: boolean;
    /** True while previewing another user. */
    active: boolean;
    userId: number | null;
    name: string | null;
}

export interface ViewAsUser {
    id: number;
    username: string;
    name: string;
    email: string;
}

/** Search every platform user (superuser only). */
export function fetchViewAsUsers(q: string): Promise<{ status: true; users: ViewAsUser[] }> {
    return api.get(`/viewAsUsers?q=${encodeURIComponent(q)}`);
}

export function startViewAs(userId: number): Promise<{ status: true; viewAs: ViewAsState }> {
    return api.post("/viewAsStart", { userId });
}

export function stopViewAs(): Promise<{ status: true; viewAs: ViewAsState }> {
    return api.post("/viewAsStop", {});
}
